import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Upload,
  Space,
  Switch,
  Tag,
  Select,
  Pagination
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { materialItems, upload, proxyImageUrl } from '../../api';

const ADMIN_MATERIAL_THUMBNAIL_OPTIONS = { width: 360, height: 300, mode: 'lfit', quality: 82 };

function MaterialItemManage() {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [batchFileList, setBatchFileList] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(48);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchData();
  }, [keyword, categoryId, page, pageSize]);

  const categoryOptions = categories.map((item) => ({
    label: `${item.name} (${item.count})`,
    value: item.id,
    name: item.name
  }));

  const getCategoryName = (selectedCategoryId) => {
    const category = categories.find((item) => Number(item.id) === Number(selectedCategoryId));
    return category?.name || '';
  };

  const fetchCategories = async () => {
    try {
      const res = await materialItems.categories();
      setCategories(res.data.data || []);
    } catch (err) {
      message.error('获取素材分类失败');
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await materialItems.list({
        include_disabled: 1,
        keyword: keyword || undefined,
        category_id: categoryId || undefined,
        page,
        pageSize
      });
      setData(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      message.error('获取素材失败');
      setTotal(0);
    }

    setLoading(false);
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ category_id: undefined, category_name: '', sort_order: 0, enabled: true });
    setModalVisible(true);
  };

  const handleBatchAdd = () => {
    batchForm.resetFields();
    batchForm.setFieldsValue({ category_id: categoryId || undefined, sort_order: 0, enabled: true });
    setBatchFileList([]);
    setBatchModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await materialItems.delete(id);
      message.success('删除成功');
      setPage(1);
      fetchData();
      fetchCategories();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleEnabledChange = async (record, enabled) => {
    try {
      await materialItems.update(record.id, {
        ...record,
        enabled
      });
      message.success(enabled ? '已启用' : '已停用');
      fetchData();
    } catch (err) {
      message.error('状态更新失败');
    }
  };

  const normalizeValues = (values) => ({
    ...values,
    category_name: getCategoryName(values.category_id)
  });

  const handleSubmit = async () => {
    try {
      const values = normalizeValues(await form.validateFields());

      if (editingItem) {
        await materialItems.update(editingItem.id, values);
        message.success('更新成功');
      } else {
        await materialItems.create(values);
        message.success('添加成功');
      }

      setModalVisible(false);
      fetchData();
      fetchCategories();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const handleBatchSubmit = async () => {
    try {
      const values = normalizeValues(await batchForm.validateFields());

      if (!batchFileList.length) {
        message.error('请先选择素材图片');
        return;
      }

      message.loading({ content: '正在上传素材...', key: 'material-item-batch' });
      const items = [];

      for (const file of batchFileList) {
        try {
          const originFile = file.originFileObj || file;
          const res = await upload.upload(originFile);
          items.push({
            name: originFile.name || file.name || '素材',
            image_url: res.data.data.url
          });
        } catch (err) {
          message.error({ content: `上传失败: ${file.name}`, key: 'material-item-batch' });
          return;
        }
      }

      const res = await materialItems.batchCreate({
        items,
        category_id: values.category_id,
        category_name: values.category_name,
        sort_order: values.sort_order,
        enabled: values.enabled
      });
      message.success({ content: `成功添加 ${res.data.data.count} 个素材`, key: 'material-item-batch' });
      setBatchModalVisible(false);
      setBatchFileList([]);
      setPage(1);
      fetchData();
      fetchCategories();
    } catch (err) {
      if (err?.errorFields) {
        return;
      }

      message.error(err.response?.data?.message || '操作失败');
    }
  };

  const handleUpload = async (file) => {
    try {
      const res = await upload.upload(file);
      form.setFieldsValue({ image_url: res.data.data.url, name: form.getFieldValue('name') || file.name });
      message.success('上传成功');
    } catch (err) {
      message.error('上传失败');
    }

    return false;
  };

  const handleSearch = (value) => {
    const nextKeyword = String(value || '').trim();
    setSearchInput(value);
    setKeyword(nextKeyword);
    setPage(1);
  };

  const handleBatchUploadChange = ({ fileList }) => {
    setBatchFileList(fileList);
  };

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>素材库</div>
            <div style={{ color: '#667085' }}>
              管理编辑器左侧“素材”面板，远程导入的素材图片会存储到 OSS。
            </div>
            <Space wrap>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ width: 160 }}>
                添加素材
              </Button>
              <Button icon={<UploadOutlined />} onClick={handleBatchAdd} style={{ width: 160 }}>
                批量添加
              </Button>
              <Select
                allowClear
                placeholder="全部分类"
                value={categoryId || undefined}
                onChange={(value) => {
                  setCategoryId(value || '');
                  setPage(1);
                }}
                style={{ width: 180 }}
                options={categoryOptions}
              />
              <Input.Search
                allowClear
                placeholder="搜索名称或分类"
                value={searchInput}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSearchInput(nextValue);

                  if (!nextValue) {
                    handleSearch('');
                  }
                }}
                onSearch={handleSearch}
                style={{ width: 280 }}
              />
            </Space>
          </Space>
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
          {data.map((item) => (
            <Card
              key={item.id}
              loading={loading}
              hoverable
              cover={
                <div style={{ height: 200, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                  <img
                    alt={item.name}
                    src={proxyImageUrl(item.image_url, ADMIN_MATERIAL_THUMBNAIL_OPTIONS)}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
              }
              actions={[
                <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
                  编辑
                </Button>,
                <Popconfirm key="delete" title="确定删除这个素材？" onConfirm={() => handleDelete(item.id)}>
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              ]}
            >
              <Space direction="vertical" size={8} style={{ width: '100%', minWidth: 0 }}>
                <div
                  title={item.name}
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.name}
                </div>
                <div style={{ minWidth: 0 }}>
                  <Tag color="blue" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.category_name || '未分类'}
                  </Tag>
                  <Tag color={item.enabled === false ? 'default' : 'success'}>
                    {item.enabled === false ? '已停用' : '已启用'}
                  </Tag>
                </div>
                <Switch
                  checked={item.enabled !== false}
                  checkedChildren="启用"
                  unCheckedChildren="停用"
                  onChange={(checked) => handleEnabledChange(item, checked)}
                />
                <div
                  title={item.image_url}
                  style={{
                    color: '#98a2b3',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.image_url}
                </div>
              </Space>
            </Card>
          ))}
        </div>

        {!data.length && !loading && (
          <Card>
            <div style={{ textAlign: 'center', color: '#98a2b3', padding: '36px 0' }}>还没有素材。</div>
          </Card>
        )}

        {total > 0 && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                showQuickJumper
                showTotal={(count) => `共 ${count} 个素材`}
                pageSizeOptions={[24, 48, 96, 120]}
                onChange={(nextPage, nextPageSize) => {
                  setPage(nextPage);
                  setPageSize(nextPageSize);
                }}
              />
            </div>
          </Card>
        )}
      </Space>

      <Modal
        title={editingItem ? '编辑素材' : '添加素材'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={640}
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="素材名称" rules={[{ required: true, message: '请输入素材名称' }]}>
            <Input placeholder="例如：箭头装饰" />
          </Form.Item>

          <Form.Item name="category_id" label="素材分类" rules={[{ required: true, message: '请选择素材分类' }]}>
            <Select placeholder="请选择素材分类" options={categoryOptions} />
          </Form.Item>

          <Form.Item name="sort_order" label="排序值">
            <Input type="number" placeholder="数值越大越靠前" />
          </Form.Item>

          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item name="image_url" label="图片地址" rules={[{ required: true, message: '请上传图片' }]}>
            <Input
              placeholder="https://img2.zhilengwang.cn/..."
              addonBefore={
                <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
                  <Button icon={<UploadOutlined />}>上传</Button>
                </Upload>
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量添加素材"
        open={batchModalVisible}
        onOk={handleBatchSubmit}
        onCancel={() => setBatchModalVisible(false)}
        width={680}
        okText="批量添加"
        maskClosable={false}
        keyboard={false}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <Form form={batchForm} layout="vertical">
          <Form.Item name="category_id" label="素材分类" rules={[{ required: true, message: '请选择素材分类' }]}>
            <Select placeholder="请选择素材分类" options={categoryOptions} />
          </Form.Item>

          <Form.Item name="sort_order" label="统一排序值">
            <Input type="number" placeholder="数值越大越靠前" />
          </Form.Item>

          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>

          <Form.Item
            name="image_urls"
            label="上传素材"
            valuePropName="fileList"
            getValueFromEvent={(event) => event?.fileList || []}
            rules={[{ required: true, message: '请上传素材图片' }]}
          >
            <Upload.Dragger
              listType="picture-card"
              multiple
              accept="image/*"
              fileList={batchFileList}
              beforeUpload={() => false}
              onChange={handleBatchUploadChange}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p>点击或拖拽素材图片到此区域</p>
              <p style={{ color: '#999', fontSize: 12 }}>
                批量添加会使用文件名作为素材名称
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default MaterialItemManage;
